<script lang="ts" setup>
import { ref } from 'vue';
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { BUCKET_DATA_TYPES } from '@/constants';
import { formatDateStringToIso } from '@/utils/date-utils';
import BucketList from '@/components/data/BucketList.vue';

// --- Component References ---
const bucketListRef = ref<InstanceType<typeof BucketList>>();

// Filter state
const bucketFriendlyNamePattern = ref('');

const dateCreatedAfter = ref<string | null>(null);
const timeCreatedAfter = ref<string | null>(null);
const dateTimeCreatedAfter = ref<string | null>(null);

const dateCreatedBefore = ref<string | null>(null);
const timeCreatedBefore = ref<string | null>(null);
const dateTimeCreatedBefore = ref<string | null>(null);

function onSearchRequested() {
  dateTimeCreatedAfter.value = formatDateStringToIso(
    dateCreatedAfter.value,
    true
  );
  dateTimeCreatedBefore.value = formatDateStringToIso(
    dateCreatedBefore.value,
    true
  );

  bucketListRef.value?.requestSearch();
}

function clearFilter() {
  bucketFriendlyNamePattern.value = '';

  dateCreatedBefore.value = null;
  timeCreatedBefore.value = null;
  dateTimeCreatedBefore.value = null;

  dateCreatedAfter.value = null;
  timeCreatedAfter.value = null;
  dateTimeCreatedAfter.value = null;

  bucketListRef.value?.requestSearch();
}
</script>

<template>
  <div>
    <h1>
      <font-awesome-icon :icon="['fas', 'database']" /> System Data Manager
    </h1>
    <h5>Browse and manage the system data including barcode whitelists</h5>
    <hr />

    <!-- Filter -->
    <div class="my-3">
      <BForm
        class="d-flex flex-row align-items-end flex-wrap gap-2"
        @submit.prevent="onSearchRequested"
      >
        <!-- Bucket friendly name -->
        <BFormGroup label="Bucket name" label-for="bucket-name">
          <BFormInput
            id="search-text"
            type="text"
            v-model="bucketFriendlyNamePattern"
          />
        </BFormGroup>
        <!-- Date created after -->
        <BFormGroup label="Created after" label-for="date-created-after">
          <BFormInput
            id="date-created-after"
            type="date"
            v-model="dateCreatedAfter"
          />
        </BFormGroup>
        <!-- Date created before -->
        <BFormGroup label="Created before" label-for="date-created-before">
          <BFormInput
            id="date-created-before"
            type="date"
            v-model="dateCreatedBefore"
          />
        </BFormGroup>
        <!-- Filter action buttons -->
        <BButton variant="outline-secondary" @click="clearFilter">
          <font-awesome-icon icon="trash" class="me-1" />
          Clear filter
        </BButton>
        <BButton
          variant="outline-dark"
          type="submit"
          :disabled="bucketListRef?.bucketsQueryInProgress"
        >
          <span v-if="bucketListRef?.bucketsQueryInProgress">
            <b-spinner small label=""></b-spinner>
            Searching
          </span>
          <span v-else>
            <font-awesome-icon icon="magnifying-glass" class="me-1" />
            Search
          </span>
        </BButton>
      </BForm>
    </div>

    <BucketList
      ref="bucketListRef"
      parentName="System Data Manager"
      messageNoItemsFound="No system buckets found."
      :across-users="true"
      :data-type="BUCKET_DATA_TYPES.SYSTEM"
      :bucket-friendly-name-pattern="bucketFriendlyNamePattern"
      :created-after="dateTimeCreatedAfter"
      :created-before="dateTimeCreatedBefore"
    />
  </div>
</template>
